import { t } from "@/i18n-js/instance";
import {
  dateFormats,
  formattedDateTime,
} from "@/react/helpers/dateTimeHelpers/formattedDateTime";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { Typography } from "@circle-react-uikit/Typography";
import { ConnectionButtons } from "../../ActionButtons/ConnectionButtons";
import { useDetermineConnectionStatus } from "../../hooks/useDetermineConnectionStatus";
import { MemberActions } from "./MemberActions";

interface CardHeaderProps {
  communityMember: any;
  shouldShowHiddenMemberTooltip?: boolean;
}

export const CardHeader = ({
  communityMember,
  shouldShowHiddenMemberTooltip = false,
}: CardHeaderProps) => {
  const shouldShowHiddenMemberIcon =
    shouldShowHiddenMemberTooltip &&
    !communityMember?.visible_in_member_directory;

  const { isActive: isConnected } = useDetermineConnectionStatus(
    communityMember?.connection_to_current_community_member,
  );

  if (!communityMember) {
    return null;
  }

  return (
    <div className="flex flex-col flex-wrap items-start justify-between gap-y-4 sm:flex-row md:gap-y-0">
      <div className="flex min-w-0 flex-1 flex-col gap-y-0 md:gap-y-1">
        <header className="flex items-center gap-x-2" data-testid="member-name">
          <Typography.LabelSm weight="semibold">
            {communityMember.name}
          </Typography.LabelSm>
          {shouldShowHiddenMemberIcon && (
            <div
              aria-label={t(
                "members_directory.community_members.hidden_profile",
              )}
            >
              <TippyV2
                placement="right"
                content={t(
                  "members_directory.community_members.hidden_profile",
                )}
              >
                <Icon type="eye-closed-solid" size={16} />
              </TippyV2>
            </div>
          )}
        </header>
        <div className="text-default hidden min-w-0 text-left md:flex">
          <Typography.LabelXs truncate>
            {communityMember.headline}
          </Typography.LabelXs>
        </div>

        {communityMember.location && (
          <div className="text-default mt-1 flex items-center gap-1">
            <Icon type="16-location" size={16} className="shrink-0" />
            <Typography.LabelXs truncate color="text-default">
              {communityMember.location}
            </Typography.LabelXs>
          </div>
        )}
      </div>

      <div className="flex items-end gap-x-2 gap-y-1 md:items-center">
        {isConnected && (
          <TippyV2
            content={t("members_directory.connected_on_hover_text", {
              date_string: formattedDateTime({
                dateTime: new Date(
                  communityMember.connection_to_current_community_member.created_at,
                ),
                format: dateFormats.short_month_and_date_with_year,
              }),
            })}
            placement="bottom"
          >
            <div className="bg-secondary text-dark flex !h-8 items-center justify-center gap-1 rounded-3xl py-[2px] pl-2 pr-3 font-medium">
              <Icon type="20-checkmark" size={20} className="size-4" />
              <Typography.LabelXs weight="medium">
                {t("profile_drawer.connect_banner.states.active")}
              </Typography.LabelXs>
            </div>
          </TippyV2>
        )}
        <ConnectionButtons member={communityMember} shouldShowOnlyIcon />
        <MemberActions communityMember={communityMember} />
      </div>
    </div>
  );
};
